import { refusalReasonWords } from '@/ui/words';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { failureOf, said } from '@/api/errors';
import { useAcknowledgeRequest, useCancelRequest, useCompleteRequest, useDecideSuggestion, useOpenSuggestionIssue, useRefuseRequest, useRequest, useRequestStanding } from '@/api/hooks';
import { StandingPanel } from '@/ui/Standing';
import type { WireRequest } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { useDepartmentLabel } from '@/app/departments';
import { Dialog } from '@/ui/Dialog';
import { Button, Field, Kicker, Skeleton } from '@/ui/primitives';
import { FactList, RecordHeader, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { actorLabel } from '@/today/copy';
import { whereItWent } from './Requests';
import { explainRequest, onItsPage } from './explain';
import { Explainer } from '@/ui/context';
import { useWho } from '@/ui/who';
import { Textarea } from '@/ui/fields';
import { ResearchAnswerView, ResearchAskView, parseResearch } from './ResearchView';
import { ResearchNext, ResearchUses } from './ResearchActions';
import { useTechnical, useToolNames } from '@/app/technical';
import { AgentBrief } from '@/ui/brief';
import { stateWords } from '@/ui/words';

/**
 * One request, from whichever side you are on. Only the receiving side
 * completes or refuses (`wrong_side` otherwise); only the requester
 * withdraws or acknowledges. The result is the only thing that crosses.
 */
export function RequestPage({ recordId }: { recordId?: string } = {}) {
  const tech = useTechnical();
  const names = useToolNames();
  const departmentLabel = useDepartmentLabel();
  const params = useParams();
  const id = recordId ?? params.id ?? null;
  const me = useMeOrThrow();
  const navigate = useNavigate();
  const request = useRequest(id);
  const r0 = request.data?.request;
  // Where it stands, while there is still something to explain (ADR-0169).
  const standing = useRequestStanding(id, r0?.state === 'open' || r0?.state === 'accepted');
  const [flow, setFlow] = useState<'complete' | 'refuse' | null>(null);
  const cancel = useCancelRequest();
  const acknowledge = useAcknowledgeRequest();
  const toast = useToast();
  const who = useWho();

  if (request.isPending) return <Skeleton rows={8} />;
  if (request.isError) return <div className="p-6"><Refusal failure={failureOf(request.error)} /></div>;
  const r = request.data.request;
  const receiver = r.toDepartment === me.department;
  const requester = r.fromDepartment === me.department;
  const live = r.state === 'open' || r.state === 'accepted';
  const grant = r.refusalReason?.startsWith('needs_grant:') ? r.refusalReason.slice('needs_grant:'.length) : null;

  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <RecordHeader
        kicker={`Request · ${departmentLabel(r.fromDepartment)} → ${departmentLabel(r.toDepartment)}`}
        title={r.title}
        subtitle={
          r.state === 'accepted'
            ? r.assigneeAgent
              ? `Routed to ${r.assigneeAgentName ?? `${departmentLabel(r.toDepartment)}'s agent`}`
              : `With ${departmentLabel(r.toDepartment)}'s lead — no agent there can do it`
            : null
        }
        meta={`Asked ${when(r.createdAt)} by ${actorLabel(r.requestedBy)}${r.decidedAt ? ` · decided ${when(r.decidedAt)}` : ''}${r.completedAt ? ` · done ${when(r.completedAt)}` : ''}`}
        chips={[whereItWent(r)]}
        actions={
          <>
            <Button icon="back" size="row" onClick={() => void navigate('/requests')}>Back to requests</Button>
            {receiver && live && (
              <>
                <Button size="row" variant="primary" onClick={() => setFlow('complete')}>Seal the result back</Button>
                <Button icon="close" size="row" onClick={() => setFlow('refuse')}>Refuse, with a reason</Button>
              </>
            )}
            {requester && live && (
              <Button
                size="row"
                disabled={cancel.isPending}
                onClick={() =>
                  cancel.mutate(r.id, {
                    onSuccess: () => toast({ text: 'Withdrawn. Their task is cancelled with it.' }),
                    onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
                  })
                }
              >
                Withdraw it
              </Button>
            )}
            {requester && r.state === 'done' && !r.acknowledgedAt && (
              <Button
                size="row"
                variant="primary"
                disabled={acknowledge.isPending}
                onClick={() =>
                  acknowledge.mutate(r.id, {
                    onSuccess: () => toast({ text: 'Seen. Both departments have the receipt.' }),
                    onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
                  })
                }
              >
                Got it
              </Button>
            )}
          </>
        }
      />

      <Explainer context={onItsPage(explainRequest(r, who, me.department), `/requests/${r.id}`)} startOpen label="About this request" />

      {live && standing.data && <StandingPanel standing={standing.data.standing} />}

      {r.body && (
        <section className="flex flex-col gap-2" aria-label="What they asked">
          <Kicker>{r.procedure === 'page_suggestion' ? 'What was suggested' : 'What was asked'}</Kicker>
          {r.procedure === 'page_suggestion' ? (
            <p className="m-0 whitespace-pre-wrap text-body">{suggestionWords(r.body)}</p>
          ) : (
            // A research ask is read as what it asks; anything else, as written.
            <ResearchAskView text={r.body} />
          )}
        </section>
      )}

      {r.result && (
        <section className="flex flex-col gap-2 rounded-brand border border-accent p-4" aria-label="The result" data-testid="result">
          <Kicker tone="accent">{r.procedure === 'page_suggestion' ? 'The spec Norum Tech wrote — for the superadmin to decide' : 'The result — all that crossed'}</Kicker>
          {r.procedure === 'page_suggestion' ? <SpecText text={r.result} /> : <ResearchAnswerView text={r.result} />}
        </section>
      )}

      {requester && r.state === 'done' && r.result && (r.procedure === 'research_market' || r.procedure === 'research_suppliers' || r.procedure === 'research_distributors') && <ResearchFollowUp requestId={r.id} result={r.result} />}

      {r.procedure === 'page_suggestion' && r.suggestionDecision && (
        <SuggestionDecided decision={r.suggestionDecision} requestId={r.id} spec={me.isSuperadmin && r.state === 'done' ? (r.result ?? null) : null} />
      )}
      {r.procedure === 'page_suggestion' && r.state === 'done' && r.result && !r.suggestionDecision && me.isSuperadmin && (
        <DecideSuggestion requestId={r.id} spec={r.result} />
      )}

      {r.state === 'refused' && (
        <section className="flex flex-col gap-2 rounded-brand border border-warning p-4" aria-label="Why not">
          <Kicker>{grant ? 'Refused: it needs a grant only a person can make' : 'Refused, with the reason'}</Kicker>
          <p className="m-0 text-body">
            {grant
              ? tech
                ? `${departmentLabel(r.toDepartment)} holds no ${grant}. A request confers nothing (§18): the receiving department does the task with what it already holds, and a grant is critical tier (§17). Your lead has a task naming whom to ask.`
                : `${departmentLabel(r.toDepartment)} cannot do this with what it has. Your lead has a task naming whom to ask.`
              : r.refusalReason ? refusalReasonWords(r.refusalReason) : null}
          </p>
        </section>
      )}

      <FactList
        facts={[
          ['From', departmentLabel(r.fromDepartment)],
          ['To', departmentLabel(r.toDepartment)],
          ['Asked by', actorLabel(r.requestedBy)],
          ['Needs', r.needs.tools.length + r.needs.scopes.length === 0 ? 'A result, not access' : names(r.needs.tools) ? `${names(r.needs.tools)}${tech && r.needs.scopes.length ? ` (${r.needs.scopes.join(', ')})` : ''}` : 'Access this department does not have'],
          ['State', stateWords(r.state)],
          ['Acknowledged', r.acknowledgedAt ? when(r.acknowledgedAt) : null],
        ]}
      />

      <AgentBrief
        lines={[
          ['request', r.id],
          ['from', r.fromDepartment],
          ['to', r.toDepartment],
          ['state', r.state],
          ['needs', [...r.needs.tools, ...r.needs.scopes].join(',') || null],
        ]}
      />

      {flow === 'complete' && <Complete request={r} onClose={() => setFlow(null)} />}
      {flow === 'refuse' && <Refuse request={r} onClose={() => setFlow(null)} />}
    </div>
  );
}

/**
 * What the asking department does with research that came back (ADR-0175):
 * the next steps the answer points to, and, for a market's research, what
 * its findings are good for. Only the asker's: the answer crossed to it.
 */
function ResearchFollowUp({ requestId, result }: { requestId: string; result: string }) {
  const parsed = parseResearch(result);
  if (parsed?.kind !== 'market_answer' && parsed?.kind !== 'supplier_answer' && parsed?.kind !== 'distributor_answer') return null;
  return (
    <>
      <ResearchNext parsed={parsed} requestId={requestId} />
      {/* A distributor search's companies are filed as uses when it arrives; a market's are found on a press. */}
      {parsed.kind !== 'supplier_answer' && <ResearchUses requestId={requestId} findable={parsed.kind === 'market_answer'} />}
    </>
  );
}

function Complete({ request, onClose }: { request: WireRequest; onClose: () => void }) {
  const complete = useCompleteRequest();
  const toast = useToast();
  const [result, setResult] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Request from another department"
      title="Seal the result back"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Send the result',
        disabled: complete.isPending,
        onClick: () => {
          if (result.trim().length < 12) return setError('A few more words — this is the whole of what they get.');
          complete.mutate(
            { requestId: request.id, result: result.trim() },
            {
              onSuccess: () => { toast({ text: 'Sealed and sent. They see the result, never the rows.' }); onClose(); },
              onError: (e) => setError(said(failureOf(e))),
            },
          );
        },
      }}
    >
      <p className="m-0 text-body">They asked; you answer with a result, not with access. What you write here is all they ever see.</p>
      <Field label="The result" error={error ?? undefined}>
        <Textarea
          aria-label="The result"
          className="min-h-[140px] w-full"
          value={result}
          onChange={(e) => setResult(e.target.value)}
          placeholder="14 invoices, Jun–Aug: amounts and dates by customer…"
        />
      </Field>
      {complete.isError && <Refusal failure={failureOf(complete.error)} compact />}
    </Dialog>
  );
}

function Refuse({ request, onClose }: { request: WireRequest; onClose: () => void }) {
  const refuse = useRefuseRequest();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Not an error"
      title="Refuse this request"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Refuse, with the reason',
        disabled: refuse.isPending,
        onClick: () => {
          if (reason.trim() === '') return setError('Say why.');
          refuse.mutate(
            { requestId: request.id, reason: reason.trim() },
            {
              onSuccess: () => { toast({ text: 'Refused. Their lead has a task with the reason.' }); onClose(); },
              onError: (e) => setError(said(failureOf(e))),
            },
          );
        },
      }}
    >
      <p className="m-0 text-body">
        A refusal is a 200, not a failure — the platform did exactly what it should. The reason becomes a task on
        their lead's day.
      </p>
      <Field label="Why not" error={error ?? undefined}>
        <Textarea
          aria-label="Why not"
          className="min-h-[100px] w-full"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="We hold no tool that reads LinkedIn…"
        />
      </Field>
      {refuse.isError && <Refusal failure={failureOf(refuse.error)} compact />}
    </Dialog>
  );
}

/** A suggestion's ask, as the person wrote it and the page it was for (0132). */
function suggestionWords(body: string): string {
  try {
    const ask = JSON.parse(body) as { pageTitle?: string; suggestion?: string };
    return ask.suggestion ? `On the ${ask.pageTitle ?? ''} page:\n\n${ask.suggestion}` : body;
  } catch {
    return body;
  }
}

/** A written-up spec: its "## " parts as headings, the rest as it was written. */
function SpecText({ text }: { text: string }) {
  const parts = text.split(/^##\s+/m).filter((p) => p.trim());
  return (
    <div className="flex flex-col gap-3" data-testid="suggestion-spec">
      {parts.map((part) => {
        const [heading, ...rest] = part.split('\n');
        return (
          <section key={heading} className="flex flex-col gap-1">
            <h3 className="m-0 text-label uppercase tracking-label text-meta">{heading!.trim()}</h3>
            <p className="m-0 whitespace-pre-wrap text-body">{rest.join('\n').trim()}</p>
          </section>
        );
      })}
    </div>
  );
}

/** Whether Tech's spec says the change pipeline can build it (the server decides the same way). */
function pipelineCanBuild(spec: string): boolean {
  const at = /^##\s*How it can be built\s*$/im.exec(spec);
  if (!at) return false;
  const rest = spec.slice(at.index + at[0].length);
  const next = /^##\s/m.exec(rest);
  const how = (next ? rest.slice(0, next.index) : rest).toLowerCase();
  return /\bpipeline\b/.test(how) && !/needs an engineer/.test(how);
}

/**
 * The superadmin's one press on a suggestion (ADR-0150). Accept sends the
 * spec shown above — exactly it — to the repository as an issue, and may
 * start a change the suggester confirms; decline needs the reason they are
 * told.
 */
function DecideSuggestion({ requestId, spec }: { requestId: string; spec: string }) {
  const toast = useToast();
  const decide = useDecideSuggestion();
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const buildable = pipelineCanBuild(spec);
  const [startChange, setStartChange] = useState(false);
  const failed = (e: unknown) => toast({ text: said(failureOf(e)), tone: 'danger' });
  return (
    <section className="flex flex-col gap-3 border border-line p-4" aria-label="Decide this suggestion" data-testid="decide-suggestion">
      <Kicker>Decide</Kicker>
      <p className="m-0 text-dense text-secondary">
        Accepting opens the spec above as an issue in the repository, labelled {buildable ? 'pipeline' : 'engineer'}, and tells the person who suggested it. Nothing is built by accepting.
      </p>
      {buildable && (
        <label className="flex min-h-tap items-center gap-2 text-dense">
          <input type="checkbox" checked={startChange} onChange={(e) => setStartChange(e.target.checked)} />
          Also start a change from it in their department — they confirm its read-back, and its lead approves, as any change
        </label>
      )}
      {!declining ? (
        <div className="flex flex-wrap gap-2">
          <Button icon="check"
            variant="primary"
            disabled={decide.isPending}
            onClick={() =>
              decide.mutate(
                { requestId, decision: 'accepted', spec, ...(startChange ? { startChange: true } : {}) },
                { onSuccess: (d) => toast({ text: d.issueUrl ? 'Accepted, and opened as an issue.' : `Accepted. ${d.issueNote ?? ''}`.trim() }), onError: failed },
              )
            }
          >Accept</Button>
          <Button icon="close" onClick={() => setDeclining(true)}>Decline</Button>
        </div>
      ) : (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            decide.mutate({ requestId, decision: 'declined', reason }, { onSuccess: () => toast({ text: 'Declined. They are told why.' }), onError: failed });
          }}
        >
          <Field label="Why not — the person who suggested it reads this">
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={1000} />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={reason.trim().length < 4 || decide.isPending}>Decline</Button>
            <Button icon="back" onClick={() => setDeclining(false)}>Back</Button>
          </div>
        </form>
      )}
    </section>
  );
}

function SuggestionDecided({ decision, requestId, spec }: { decision: NonNullable<WireRequest['suggestionDecision']>; requestId: string; spec: string | null }) {
  const accepted = decision.decision === 'accepted';
  const toast = useToast();
  const open = useOpenSuggestionIssue();
  return (
    <section className={`flex flex-col gap-2 border p-4 ${accepted ? 'border-accent' : 'border-line'}`} aria-label="What was decided">
      <Kicker tone={accepted ? 'accent' : undefined}>{accepted ? 'Accepted, to build' : 'Declined'}</Kicker>
      <p className="m-0 text-dense">
        {decision.decidedBy ? `${decision.decidedBy} decided this ${when(decision.decidedAt)}.` : `Decided ${when(decision.decidedAt)}.`}
        {decision.reason ? ` ${decision.reason}` : ''}
      </p>
      {decision.issueUrl && (
        <p className="m-0 text-dense">
          <a href={decision.issueUrl} target="_blank" rel="noreferrer" className="text-accent underline">Open the issue in the repository</a>
        </p>
      )}
      {accepted && !decision.issueUrl && decision.issueNote && <p className="m-0 text-dense text-meta">{decision.issueNote}</p>}
      {/* The superadmin's own press, with the spec they read: GitHub refused when it was accepted, and nothing else would open it (ADR-0156). */}
      {accepted && !decision.issueUrl && spec && (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-dense text-secondary">No issue was opened for this yet. Once the App may write issues, this opens the spec above as one, labelled as it would have been.</p>
          <div>
            <Button icon="open"
              variant="primary"
              disabled={open.isPending}
              onClick={() =>
                open.mutate(
                  { requestId, spec },
                  { onSuccess: () => toast({ text: 'Opened as an issue.' }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) },
                )
              }
            >Open the issue now</Button>
          </div>
        </div>
      )}
      {decision.changeRequestId && (
        <p className="m-0 text-dense">
          A change was started from it. <Link to={`/changes/${decision.changeRequestId}`} className="text-accent underline">Follow the change</Link>
        </p>
      )}
    </section>
  );
}
